/**
 * The logic behind the review screen, kept out of the view so it can be tested.
 *
 * V1 section 9 asks this screen to "show exactly which revision/hash is
 * approved". That is not a label; it is a claim about bytes, and every rule in
 * this file exists so the claim is true or the approval button is unavailable.
 *
 * WHAT THE APPROVAL IS BOUND TO
 *   `app.reviews.approved_content_hash` is not optional in the schema and is
 *   compared against nothing by the database. PUB-01 is the intent: an approval
 *   binds to the exact content reviewed, so a later edit invalidates it by
 *   changing the hash rather than by rewriting the approval row. Since no
 *   migration computes or verifies that hash, this module does: it takes the
 *   draft's file list, sorts it, and digests a manifest of every path and
 *   content hash. Change any file and the binding changes; the stored approval
 *   no longer matches, and this screen says so instead of showing a stale tick.
 *
 * WHO MAY DECIDE
 *   V1 section 6 separates the author from the reviewer, and since `0034` the
 *   DATABASE enforces it: the `reviews_insert` policy refuses a self-review
 *   unless `organizations.allow_self_review` is set AND the row declares
 *   `self_review` (`0034_reviewer_separation.sql:85-90`), and
 *   `app.approve_draft` refuses the author outright before it looks at any
 *   parameter (`0033_lifecycle_rpcs.sql:957-963`). An earlier version of this
 *   comment said the policy never compared reviewer to author and that
 *   `allow_self_review` was read by nothing; both were true of `0002` and
 *   `0033` before `0034` replaced them, and neither is true now.
 *
 *   THIS MODULE NOW HONOURS THE EXCEPTION, and it did not for a long time.
 *   The sentence above used to end with "the pilot exception is unreachable
 *   from this product (nothing routes a write to `allow_self_review`), so a
 *   screen that honoured a flag nobody can set would offer an action with no
 *   path to it". That was TRUE until `0061_self_review_policy_rpc.sql` added
 *   `app.set_self_review_policy` and `views/admin.ts` grew the control that
 *   calls it — and then it was FALSE, in a deployment whose own admin screen
 *   could set the flag, while this module still refused the author
 *   unconditionally and told them that "nothing in this deployment can set
 *   it". A refusal that misstates its own cause sends a person to look for a
 *   setting that already exists.
 *
 *   So the exception is read (`SelfReviewException`) and honoured, and the
 *   refusal that remains — for an organization that has NOT opted in, and for
 *   a setting this screen could not read — names the cause that is true.
 */

import type { DraftFileRow, DraftRow, ReleaseFileRow, ReviewRow } from '../api/queries.js';
import { sha256Hex } from './sha256.js';

export const REVIEW_CHECKS: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'instructions', label: 'Instructions checked' },
  { id: 'references', label: 'References and confidentiality checked' },
  { id: 'checkpoints', label: 'Human checkpoints checked' },
];

interface BindingEntry {
  readonly path: string;
  readonly contentHash: string;
  readonly bytes: number;
}

export interface ContentBinding {
  /**
   * A READABLE rendering of the same entries, for the reviewer to look at.
   *
   * NOT the string that was digested, and named for that reason. The digested
   * string separates its fields with NUL and ends each entry with LF; rendered
   * into a `<pre>` (`views/review.ts:278`) a NUL shows as nothing at all, so
   * displaying the digested bytes would show the reviewer a wall of collapsed
   * whitespace and would not be the string the hash is over. `display` and
   * `hash` are deliberately different fields with different meanings, so that
   * a later reader cannot conflate them back into one.
   */
  readonly display: string;
  /** THE digest an approval is written against. See `contentBinding`. */
  readonly hash: string;
  readonly entries: readonly BindingEntry[];
}

/**
 * The digest an approval is written against — migration 0047's rule, byte for
 * byte, not this file's own earlier opinion.
 *
 *   manifest := for each entry, ordered by the UTF-8 BYTES of `path` ascending:
 *       path NUL sha256:<64 lowercase hex> NUL <decimal byte count> LF
 *   digest   := "sha256:" + hex(sha256(utf8(manifest)))
 *
 * WHY THIS FUNCTION USED TO DISAGREE WITH THE DATABASE. It computed
 * `aquifer-draft-binding/1\n` followed by `path US content_hash` joined with
 * LF — a different string in a different order with no byte counts — while
 * `app.approve_draft` records `app._draft_content_hash`. It therefore never
 * matched any real approval, and the check at `reviewModel`'s caller was
 * reporting every approved draft as stale. The same serialisation is written
 * in four other places in this repository (`packages/worker/src/digest.ts`,
 * `apps/api/src/manifest.ts`, `packages/db-tests/src/manifest-digest.ts`,
 * and `app._content_hash`), and 0047 made the database agree with them. This
 * is the fifth, and the last.
 *
 * REIMPLEMENTED rather than imported, because `apps/web` has no workspace
 * dependency at all — it is a browser bundle, and the modules above reach
 * `node:crypto`. So the layout is written out here, and the spec that keeps it
 * honest is `packages/db-tests/src/pub-01-digest-unification.spec.ts`, which
 * pins the same layout against digests computed outside this repository.
 *
 * TWO DETAILS THAT ARE NOT COSMETIC
 *   * The sort is over UTF-8 BYTES. `left.path < right.path` compares UTF-16
 *     code units, which orders any non-BMP path differently from its bytes and
 *     would make this disagree with the other four on a path nobody has
 *     written yet. `Buffer.compare` is unavailable here, so it is done by hand
 *     over the encoded bytes.
 *   * The recorded hash is NORMALISED to the prefixed lowercase spelling before
 *     it goes in, because `draft_files.content_hash` is written by several
 *     services and may carry a bare hex digest. Two spellings of one digest
 *     must not produce two manifest digests.
 */
export function contentBinding(files: readonly DraftFileRow[]): ContentBinding {
  const encoder = new TextEncoder();
  const entries: BindingEntry[] = [...files]
    .map((file) => ({
      path: file.path,
      contentHash: normaliseContentHash(file.content_hash),
      bytes: typeof file.bytes === 'number' ? file.bytes : Number(file.bytes),
    }))
    .sort((left, right) => compareUtf8(encoder.encode(left.path), encoder.encode(right.path)));

  const NUL = String.fromCharCode(0);
  const manifest = entries
    .map((entry) => `${entry.path}${NUL}${entry.contentHash}${NUL}${entry.bytes}\n`)
    .join('');

  return {
    display: [
      'aquifer-draft-binding/1 (display only — the digest is over the NUL-separated lines)',
      ...entries.map((entry) => `${entry.path}  ${entry.contentHash}  ${entry.bytes} bytes`),
    ].join('\n'),
    hash: `sha256:${sha256Hex(manifest)}`,
    entries,
  };
}

/** One spelling of a digest: `sha256:` and lowercase hex, or nothing. */
function normaliseContentHash(recorded: string): string {
  const trimmed = recorded.trim();
  const bare = /^sha256:(.*)$/i.exec(trimmed);
  const body = (bare === null ? trimmed : (bare[1] as string)).toLowerCase();
  return `sha256:${body}`;
}

/** Byte order, the way `Buffer.compare` orders and the way the digest requires. */
function compareUtf8(left: Uint8Array, right: Uint8Array): number {
  const shared = Math.min(left.length, right.length);
  for (let index = 0; index < shared; index += 1) {
    const difference = (left[index] as number) - (right[index] as number);
    if (difference !== 0) return difference;
  }
  return left.length - right.length;
}

type ChangeKind = 'added' | 'changed' | 'unchanged' | 'removed';

export interface ChangedFile {
  readonly path: string;
  readonly kind: ChangeKind;
  readonly beforeHash: string | null;
  readonly afterHash: string | null;
}

/**
 * Which files a draft changes, computed from the two content-hash lists.
 *
 * This is a file-level change set, not a line diff, and the reason is a fact
 * about the deployment rather than a shortcut: neither surface serves file
 * bodies. `apps/api` serves the packaged bundle as one markdown document, and
 * `app.draft_files.storage_key` names an object this build has no read path for.
 * Two honest lists beat one invented hunk.
 */
export function changedFiles(
  baseFiles: readonly ReleaseFileRow[],
  draftFiles: readonly DraftFileRow[],
): readonly ChangedFile[] {
  const before = new Map(baseFiles.map((file) => [file.path, file.content_hash]));
  const after = new Map(draftFiles.map((file) => [file.path, file.content_hash]));
  const paths = [...new Set([...before.keys(), ...after.keys()])].sort();
  return paths.map((path) => {
    const beforeHash = before.get(path) ?? null;
    const afterHash = after.get(path) ?? null;
    const kind: ChangeKind =
      beforeHash === null ? 'added' : afterHash === null ? 'removed' : beforeHash === afterHash ? 'unchanged' : 'changed';
    return { path, kind, beforeHash, afterHash };
  });
}

/**
 * What the organization has said about the audited self-review exception.
 *
 * THREE STATES, and the third is the point.
 *
 *   `allowed`      the organization opted in — `organizations.allow_self_review`
 *                  is true — so the author may review their own draft. The
 *                  database admits it on exactly that condition
 *                  (`0034_reviewer_separation.sql:80-92`), and records
 *                  `reviews.self_review` so the use is visible in the data.
 *   `not-allowed`  the organization has not opted in. The author may not
 *                  review their own draft, and the refusal says so.
 *   `unknown`      the setting could not be READ. That is NOT the same fact as
 *                  `not-allowed`, and a screen that collapsed the two would
 *                  refuse with a cause it had not established — the same
 *                  falsehood as the sentence this file used to carry, wearing
 *                  a different hat. It fails CLOSED, because the database
 *                  admits a self-review only when the organization has enabled
 *                  the exception and this screen cannot tell whether it has.
 *
 * A BOOLEAN CANNOT CARRY THIS. `false` would mean "the organization has not
 * enabled it" whether the setting was read and said no or was never read at
 * all, and the refusal for those two cases is a different sentence.
 */
export type SelfReviewException =
  | { readonly kind: 'allowed' }
  | { readonly kind: 'not-allowed' }
  | { readonly kind: 'unknown'; readonly reason: string };

export type Decision =
  | {
      readonly kind: 'approve';
      readonly binding: ContentBinding;
      /**
       * Whether this approval is the author reviewing their own draft.
       *
       * Carried so the screen can say so, not so it can decide it: the
       * database sets `reviews.self_review` from the same fact
       * (`0033_lifecycle_rpcs.sql:955`), and a screen that offered the
       * self-review path without saying so would hide an audited use of an
       * exception from the person using it.
       */
      readonly selfReview: boolean;
    }
  | { readonly kind: 'request-changes' }
  | { readonly kind: 'blocked'; readonly reason: string };

export interface DecisionInput {
  readonly draft: DraftRow;
  readonly files: readonly DraftFileRow[];
  readonly viewerId: string;
  readonly checks: Readonly<Record<string, boolean>>;
  /** The most recent approval on this draft, if there is one. */
  readonly latestApproval: ReviewRow | null;
  /**
   * Whether this organization has enabled the audited self-review exception.
   *
   * NOT OPTIONAL, because a missing value is not the same as "off": a screen
   * that treated it as off would refuse with a cause it had not established,
   * and one that treated it as on would offer an action the database refuses.
   */
  readonly selfReview: SelfReviewException;
}

function outstandingChecks(checks: Readonly<Record<string, boolean>>): readonly string[] {
  return REVIEW_CHECKS.filter((check) => checks[check.id] !== true).map((check) => check.label);
}

/**
 * Decide what this reviewer may do with this draft right now.
 *
 * The order matters. Self-review is refused before the checklist is read,
 * because a reviewer who cannot decide should be told why rather than told to
 * tick four boxes first. An approval whose hash no longer matches the content is
 * refused before the checklist too, because the thing being approved has
 * changed and no checklist can make that safe.
 *
 * THE SELF-REVIEW BRANCH IS NOW A QUESTION WITH AN ANSWER, not a flat refusal.
 * The database asks the organization (`0033_lifecycle_rpcs.sql:948-960`,
 * `0034_reviewer_separation.sql:80-92`) and this screen asks the same
 * question of the same value. When the answer is yes, the author is a
 * reviewer — the decision is the same decision anybody else would make, and it
 * is marked as the self-review it is so the screen can say that the use is
 * recorded and audited.
 */
export function decide(input: DecisionInput): Decision {
  const { draft, files, viewerId, checks, latestApproval, selfReview } = input;

  if (draft.state !== 'in_review') {
    return {
      kind: 'blocked',
      reason: `This draft is in state "${draft.state}". Only a draft that is in review can be decided here.`,
    };
  }

  const isAuthor = draft.author_id === viewerId;
  if (isAuthor) {
    // THE EXCEPTION IS PERMITTED: fall through to the ordinary decision, which
    // is marked as a self-review below. The organization asked for this and
    // the database agrees, so refusing here would be the screen disagreeing
    // with the thing it is a view of.
    if (selfReview.kind === 'unknown') {
      return {
        kind: 'blocked',
        reason:
          'You are the author of this draft, and V1 section 6 separates the author from the reviewer. ' +
          "This screen could not read the organization's self-review setting, so it cannot say whether " +
          'the audited self-review exception applies, and it will not offer a decision it cannot ' +
          `check: ${selfReview.reason}`,
      };
    }
    if (selfReview.kind === 'not-allowed') {
      return {
        kind: 'blocked',
        reason:
          'You are the author of this draft. V1 section 6 separates the author from the reviewer, and ' +
          'the database enforces that too, so this decision would be refused regardless. This ' +
          'organization has not enabled the audited self-review exception. An organization ' +
          'administrator can enable it in Administration, under Publishing policy, and every use of it ' +
          'is recorded on the review row and in the audit trail.',
      };
    }
  }

  const binding = contentBinding(files);
  if (binding.entries.length === 0) {
    return {
      kind: 'blocked',
      reason:
        'This draft has no files, so there is no content for an approval to bind to. An approval without a content hash would approve nothing.',
    };
  }

  if (
    latestApproval !== null &&
    latestApproval.decision === 'approved' &&
    latestApproval.approved_content_hash !== null &&
    latestApproval.approved_content_hash !== binding.hash
  ) {
    return {
      kind: 'blocked',
      reason: `The stored approval is bound to content hash ${latestApproval.approved_content_hash}, and the draft's files now hash to ${binding.hash}. The content changed after that approval, so it must be reviewed again.`,
    };
  }

  const outstanding = outstandingChecks(checks);
  if (outstanding.length > 0) {
    return { kind: 'blocked', reason: `Still to confirm: ${outstanding.join(', ')}.` };
  }

  return { kind: 'approve', binding, selfReview: isAuthor };
}

/**
 * Whether this caller may request changes, which never needs the checklist.
 *
 * The same rule the database applies, on the same condition:
 * `app.request_changes` refuses the author of the draft unless the
 * organization has enabled the audited self-review exception
 * (`0033_lifecycle_rpcs.sql:1131-1137`). A screen that refused what the
 * database admits would be the same disagreement in a second control.
 */
export function mayRequestChanges(
  draft: DraftRow,
  viewerId: string,
  selfReview: SelfReviewException,
): Decision {
  if (draft.state !== 'in_review') {
    return {
      kind: 'blocked',
      reason: `This draft is in state "${draft.state}". A change request belongs to a draft that is in review.`,
    };
  }
  if (draft.author_id === viewerId && selfReview.kind !== 'allowed') {
    return {
      kind: 'blocked',
      reason:
        selfReview.kind === 'unknown'
          ? 'You wrote this draft, so you cannot request changes to it, and this screen could not ' +
            "read the organization's self-review setting to say whether the audited exception " +
            `applies: ${selfReview.reason}`
          : 'You wrote this draft, so you cannot request changes to it, and this organization has ' +
            'not enabled the audited self-review exception.',
    };
  }
  return { kind: 'request-changes' };
}

/** Checkpoint annotations, as the reviewer must see them: defined, not executed. */
const CHECKPOINT = /<!--\s*aquifer:checkpoint\s+([^>]*?)-->/g;

export interface CheckpointNote {
  readonly id: string;
  readonly status: string;
  readonly line: number;
}

/**
 * Find the checkpoints declared in a skill document.
 *
 * `skill-format` renders an annotation such as
 * `<!-- aquifer:checkpoint id=cp-1 status=required -->` as visible text to a
 * reviewer rather than hiding it. This reads the same declaration so the review
 * screen can list what the document asks a human to confirm. It reports what is
 * declared and never reports that anything was carried out.
 */
export function parseCheckpoints(markdown: string): readonly CheckpointNote[] {
  const found: CheckpointNote[] = [];
  for (const match of markdown.matchAll(CHECKPOINT)) {
    const attributes = match[1] ?? '';
    const id = /\bid="?([a-zA-Z0-9_-]+)"?/.exec(attributes)?.[1] ?? 'unnamed';
    const status = /\bstatus="?([a-zA-Z0-9_-]+)"?/.exec(attributes)?.[1] ?? 'required';
    found.push({
      id,
      status,
      line: markdown.slice(0, match.index).split('\n').length,
    });
  }
  return found;
}